'use client';

import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type ModalRenderer = (ctx: { close: () => void; isTop: boolean }) => ReactNode;

interface ModalEntry {
  id: string;
  render: ModalRenderer;
}

interface ModalStackContextValue {
  openModal: (render: ModalRenderer, options?: { id?: string; replace?: boolean }) => string;
  closeModal: (id: string) => void;
  closeAll: () => void;
}

const ModalStackContext = createContext<ModalStackContextValue | undefined>(undefined);

export const useModalStack = () => {
  const ctx = useContext(ModalStackContext);
  if (!ctx) {
    throw new Error('useModalStack must be used within a ModalStackProvider');
  }
  return ctx;
};

const baseZIndex = 2000;

const ModalStackRenderer: FC<{
  modals: ModalEntry[];
  closeModal: (id: string) => void;
}> = ({ modals, closeModal }) => {
  if (typeof document === 'undefined') return null;
  if (modals.length === 0) return null;

  return createPortal(
    <div className="modal-stack-container" style={{ pointerEvents: 'none' }}>
      {modals.map((modal, index) => (
        <div
          key={modal.id}
          style={{ zIndex: baseZIndex + index, pointerEvents: 'none' }}
          className="modal-stack-item fixed inset-0"
        >
          {modal.render({
            close: () => closeModal(modal.id),
            isTop: index === modals.length - 1,
          })}
        </div>
      ))}
    </div>,
    document.body,
  );
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `modal-${Math.random().toString(36).slice(2, 9)}`;
};

export const ModalStackProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<ModalEntry[]>([]);

  const closeModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const closeAll = useCallback(() => {
    setModals([]);
  }, []);

  const openModal = useCallback(
    (render: ModalRenderer, options?: { id?: string; replace?: boolean }) => {
      const modalId = options?.id || randomId();

      setModals((prev) => {
        // Replace existing modal with same id when requested
        const filtered = options?.replace ? prev.filter((entry) => entry.id !== modalId) : prev;

        // Prevent duplicate entries with the same id when not replacing
        if (!options?.replace && filtered.some((entry) => entry.id === modalId)) {
          return filtered;
        }

        return [...filtered, { id: modalId, render }];
      });

      return modalId;
    },
    [],
  );

  const value = useMemo(
    () => ({
      openModal,
      closeModal,
      closeAll,
    }),
    [closeAll, closeModal, openModal],
  );

  return (
    <ModalStackContext.Provider value={value}>
      {children}
      <ModalStackRenderer modals={modals} closeModal={closeModal} />
    </ModalStackContext.Provider>
  );
};
